/// <reference types="vite/client" />

import { ERROR_CODES } from "@monorepo/config";

// ----------------------------------------------------------------------
// Interfaces & Types
// ----------------------------------------------------------------------

/**
 * API Error Type
 * Matches the backend error response format
 */
export interface ApiError {
  code: string;
  message: string;
  status: number;
  details?: Record<string, unknown>;
  request_id?: string;
}

/**
 * Client Error Type
 * For client-side errors
 */
export interface ClientError {
  code: string;
  message: string;
  originalError?: Error;
}

/**
 * Network Error Type
 */
export interface NetworkError extends ClientError {
  code: typeof ERROR_CODES.NETWORK_ERROR | typeof ERROR_CODES.TIMEOUT_ERROR;
  isRetryable: true;
}

// ----------------------------------------------------------------------
// Type Guards
// ----------------------------------------------------------------------

export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    "message" in error &&
    "status" in error
  );
}

export function isNetworkError(error: unknown): error is NetworkError {
  return (
    typeof error === "object" &&
    error !== null &&
    "isRetryable" in error &&
    (error as any).isRetryable === true
  );
}

export function isRetryable(
  error: ApiError | ClientError | NetworkError | unknown
): boolean {
  if (isNetworkError(error)) {
    return true;
  }

  if (isApiError(error)) {
    // Retry on 408 (Timeout), 429 (Rate Limit), and 5xx (Server Errors)
    const retryableStatuses = [408, 429, 500, 502, 503, 504];
    return retryableStatuses.includes(error.status);
  }

  return false;
}

/**
 * Check if an error indicates a token expiry
 */
export function isTokenExpiredError(error: unknown): boolean {
  if (!isApiError(error)) return false;
  return (
    error.code === ERROR_CODES.TOKEN_EXPIRED ||
    error.code === ERROR_CODES.PASSWORD_RESET_TOKEN_EXPIRED ||
    error.code === ERROR_CODES.RECOVERY_TOKEN_EXPIRED
  );
}

/**
 * Check if an error indicates an invalid token
 */
export function isTokenInvalidError(error: unknown): boolean {
  if (!isApiError(error)) return false;
  return (
    error.code === ERROR_CODES.TOKEN_INVALID ||
    error.code === ERROR_CODES.PASSWORD_RESET_TOKEN_INVALID ||
    error.code === ERROR_CODES.RECOVERY_TOKEN_INVALID
  );
}

/**
 * Extract rate limit retry-after time from error
 */
export function getRateLimitRetryAfter(error: unknown): number | undefined {
  if (isApiError(error) && error.details?.retry_after) {
    const retryAfter = error.details.retry_after;
    return typeof retryAfter === "number" ? retryAfter : undefined;
  }
  return undefined;
}

// ----------------------------------------------------------------------
// Error Factories & Mappers
// ----------------------------------------------------------------------

/**
 * Map API error response to ApiError
 */
export function mapApiError(response: unknown, status?: number): ApiError {
  // 1. Standard Wrapper Format: { error: { code, message, ... } }
  if (
    response &&
    typeof response === "object" &&
    "error" in response &&
    typeof (response as any).error === "object"
  ) {
    const err = (response as any).error;
    return {
      code: err.code || ERROR_CODES.SERVER_ERROR,
      message: err.message || "An unexpected error occurred",
      status: err.status || status || 500,
      details: err.details,
      request_id: err.request_id,
    };
  }

  // 2. Flat Format: { code, message }
  if (response && typeof response === "object") {
    const err = response as Record<string, any>;
    return {
      code: err.code || ERROR_CODES.SERVER_ERROR,
      message: err.message || "An unexpected error occurred",
      status: status || 500,
      details: err.details,
      request_id: err.request_id,
    };
  }

  // 3. Fallback for strings or unknowns
  return {
    code: ERROR_CODES.SERVER_ERROR,
    message:
      typeof response === "string" ? response : "An unexpected error occurred",
    status: status || 500,
  };
}

export function createNetworkError(
  message: string,
  originalError?: Error
): NetworkError {
  return {
    code: ERROR_CODES.NETWORK_ERROR,
    message,
    isRetryable: true,
    originalError,
  };
}

export function createTimeoutError(): NetworkError {
  return {
    code: ERROR_CODES.TIMEOUT_ERROR,
    message: "Request timed out. Please check your connection and try again.",
    isRetryable: true,
  };
}

// ----------------------------------------------------------------------
// User Facing Messages
// ----------------------------------------------------------------------

/**
 * Get user-friendly error message for error code
 */
export function getUserMessageForErrorCode(
  code: string,
  defaultMessage?: string
): string {
  const errorMessages: Record<string, string> = {
    // --- Validation ---
    [ERROR_CODES.VALIDATION_ERROR]: "Please check your input and try again.",
    [ERROR_CODES.INVALID_EMAIL]: "Please enter a valid email address.",
    [ERROR_CODES.PASSWORD_TOO_WEAK]:
      "Password does not meet security requirements.",
    [ERROR_CODES.PASSWORD_RECENTLY_USED]: "You cannot reuse a recent password.",

    // --- Signin ---
    [ERROR_CODES.INVALID_CREDENTIALS]: "Invalid email or password.",
    [ERROR_CODES.ACCOUNT_LOCKED]:
      "Account locked due to too many failed attempts.",
    [ERROR_CODES.ACCOUNT_DISABLED]:
      "This account has been disabled. Contact support.",

    // --- OTP ---
    [ERROR_CODES.INVALID_OTP]:
      "The code you entered is incorrect. Please try again.",
    [ERROR_CODES.OTP_EXPIRED]:
      "This code has expired. Please request a new one.",
    [ERROR_CODES.OTP_MAX_ATTEMPTS]:
      "Too many incorrect attempts. Please request a new code.",

    // --- MFA ---
    [ERROR_CODES.TOTP_INVALID]:
      "Invalid authentication code. Please try again.",
    [ERROR_CODES.INVALID_MFA_CODE]:
      "Invalid authentication code. Please try again.",
    [ERROR_CODES.INVALID_BACKUP_CODE]: "Invalid backup code. Please try again.",
    [ERROR_CODES.WEBAUTHN_NOT_SUPPORTED]:
      "Your browser does not support biometric authentication.",
    [ERROR_CODES.WEBAUTHN_FAILED]:
      "Biometric authentication failed. Please try again.",
    [ERROR_CODES.WEBAUTHN_VERIFICATION_FAILED]:
      "Security key verification failed.",

    // --- Account Recovery ---
    [ERROR_CODES.PASSWORD_RESET_TOKEN_INVALID]:
      "This reset link is invalid or has expired.",
    [ERROR_CODES.PASSWORD_RESET_TOKEN_EXPIRED]:
      "This reset link has expired. Please request a new one.",
    [ERROR_CODES.RECOVERY_TOKEN_INVALID]:
      "This recovery link is invalid or has expired.",
    [ERROR_CODES.RECOVERY_TOKEN_EXPIRED]:
      "This recovery link has expired. Please request a new one.",
    [ERROR_CODES.RECOVERY_REQUEST_NOT_FOUND]:
      "Recovery request not found. Please try submitting a new request.",
    [ERROR_CODES.RECOVERY_REQUEST_DENIED]:
      "Your recovery request was not approved. Please contact support.",
    // Advanced Recovery
    [ERROR_CODES.INVALID_RECOVERY_SESSION]: "Session invalid or expired. Please start over.",
    [ERROR_CODES.CHANNEL_MISMATCH]: "Security check failed. Please restart recovery.",
    [ERROR_CODES.AUTHORITY_REQUIRED]: "Additional verification required.",
    [ERROR_CODES.INVALID_VERIFICATION_TOKEN]: "Invalid verification proof. Please try again.",

    // --- Session ---
    [ERROR_CODES.TOKEN_EXPIRED]: "Your session has expired. Please start over.",
    [ERROR_CODES.TOKEN_INVALID]: "Invalid session. Please start over.",
    [ERROR_CODES.SESSION_EXPIRED]:
      "Your session has expired. Please sign in again.",
    [ERROR_CODES.SSO_REQUIRED]: "This account requires Single Sign-On.",

    // --- Infrastructure ---
    [ERROR_CODES.NETWORK_ERROR]:
      "Network error. Please check your connection and try again.",
    [ERROR_CODES.TIMEOUT_ERROR]: "Request timed out. Please try again.",
    [ERROR_CODES.SERVER_ERROR]:
      "Something went wrong on our end. Please try again later.",
    [ERROR_CODES.SERVICE_UNAVAILABLE]:
      "Service temporarily unavailable. Please try again in a few moments.",
    [ERROR_CODES.RATE_LIMITED]:
      "Too many requests. Please wait a moment and try again.",
    [ERROR_CODES.RATE_LIMIT_EXCEEDED]:
      "Too many requests. Please wait a moment and try again.",
  };

  return (
    errorMessages[code] ||
    defaultMessage ||
    "An unexpected error occurred. Please try again."
  );
}

/**
 * Get user-friendly error title for error code
 */
export function getErrorTitle(code: string): string {
  const errorTitles: Record<string, string> = {
    [ERROR_CODES.INVALID_OTP]: "Invalid Code",
    [ERROR_CODES.OTP_EXPIRED]: "Code Expired",
    [ERROR_CODES.OTP_MAX_ATTEMPTS]: "Too Many Attempts",
    [ERROR_CODES.TOKEN_EXPIRED]: "Session Expired",
    [ERROR_CODES.SESSION_EXPIRED]: "Session Expired",
    [ERROR_CODES.WEBAUTHN_FAILED]: "Authentication Failed",
    [ERROR_CODES.RATE_LIMITED]: "Too Many Requests",
    [ERROR_CODES.RATE_LIMIT_EXCEEDED]: "Too Many Requests",
    [ERROR_CODES.SERVER_ERROR]: "Server Error",
    [ERROR_CODES.SERVICE_UNAVAILABLE]: "Service Unavailable",
    [ERROR_CODES.NETWORK_ERROR]: "Connection Error",
    [ERROR_CODES.TIMEOUT_ERROR]: "Connection Error",
    [ERROR_CODES.INVALID_CREDENTIALS]: "Sign In Failed",
    [ERROR_CODES.PASSWORD_RESET_TOKEN_INVALID]: "Invalid Link",
    [ERROR_CODES.PASSWORD_RESET_TOKEN_EXPIRED]: "Link Expired",
    [ERROR_CODES.RECOVERY_REQUEST_DENIED]: "Request Denied",
    [ERROR_CODES.RECOVERY_REQUEST_NOT_FOUND]: "Not Found",
    [ERROR_CODES.ACCOUNT_LOCKED]: "Account Locked",
    // Advanced Recovery
    [ERROR_CODES.INVALID_RECOVERY_SESSION]: "Session Invalid",
    [ERROR_CODES.CHANNEL_MISMATCH]: "Security Alert",
    [ERROR_CODES.AUTHORITY_REQUIRED]: "Verification Required",
    [ERROR_CODES.INVALID_VERIFICATION_TOKEN]: "Access Denied",
  };

  return errorTitles[code] || "Error";
}

/**
 * Format error for display
 */
export function formatErrorForDisplay(
  error: ApiError | ClientError | NetworkError
): {
  title: string;
  message: string;
  code: string;
} {
  return {
    title: getErrorTitle(error.code),
    message: getUserMessageForErrorCode(error.code, error.message),
    code: error.code,
  };
}

// ----------------------------------------------------------------------
// Logging & Utilities
// ----------------------------------------------------------------------

/**
 * Log error to console (with request_id if available)
 */
export function logError(
  error: ApiError | ClientError | NetworkError | unknown,
  context?: string
) {
  if (!import.meta.env.DEV) return;

  // Safe casting for logging
  const safeError = error as Partial<ApiError & ClientError>;

  const errorInfo = {
    context,
    code: safeError.code,
    message: safeError.message,
    ...(isApiError(error) && {
      status: error.status,
      request_id: error.request_id,
      details: error.details,
    }),
    timestamp: new Date().toISOString(),
  };

  console.error("[Error]", errorInfo);
}

/**
 * Retry helper function with Exponential Backoff
 */
/**
 * Calculate backoff delay
 */
export function calculateBackoffDelay(
  attempt: number,
  baseDelay: number
): number {
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  const jitter = Math.random() * 0.3 * exponentialDelay; // 30% jitter
  return Math.min(exponentialDelay + jitter, 30000); // Max 30 seconds
}

export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: unknown) {
      lastError = error;

      if (!isRetryable(error)) {
        throw error;
      }

      if (attempt < maxRetries - 1) {
        const delay = calculateBackoffDelay(attempt, baseDelay);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError!;
}
